import React, { useState, useEffect, useMemo } from "react";
import { View, StyleSheet, Platform } from "react-native";

import { useTicker } from "../context/TickerContext";
import { useAuth } from "../context/AuthContext";
import { theme, useBreakpoint } from "../constants/theme";

import NavigationHeader from "../components/NavigationHeader";
import ChannelsSidebar from "../components/community/ChannelsSidebar";
import MembersList from "../components/community/MembersList";
import ChannelContent from "../components/community/ChannelContent";
import { ScreenBackground } from "../components/ThemeGradients";

import {
  subscribeToChannels,
  subscribeToMemberPresence,
  initPresence,
} from "../services/communityService";

const COMMUNITY_ID = "main";

export default function CommunityScreen() {
  const { totalHeaderHeight } = useTicker();
  const { user } = useAuth();
  const bp = useBreakpoint();

  const [categories, setCategories] = useState([]);
  const [channelsLoading, setChannelsLoading] = useState(true);
  const [members, setMembers] = useState([]);
  const [activeChannelId, setActiveChannelId] = useState(null);

  // Auto-select the first available channel once categories load
  useEffect(() => {
    if (activeChannelId || !categories.length) return;
    const firstChannel = categories[0]?.channels?.[0];
    if (firstChannel) setActiveChannelId(firstChannel.id);
  }, [categories, activeChannelId]);

  const { activeChannelName, activeCategoryId } = useMemo(() => {
    for (const cat of categories) {
      for (const ch of cat.channels ?? []) {
        if (ch.id === activeChannelId)
          return { activeChannelName: ch.displayName ?? ch.name ?? ch.id, activeCategoryId: cat.id };
        for (const sub of ch.subChannels ?? []) {
          if (sub.id === activeChannelId)
            return { activeChannelName: sub.displayName ?? sub.name ?? sub.id, activeCategoryId: cat.id };
        }
      }
    }
    return { activeChannelName: null, activeCategoryId: null };
  }, [categories, activeChannelId]);

  const displayMembers = useMemo(
    () => members.map((m) => m.uid === user?.uid ? { ...m, onlineStatus: "online" } : m),
    [members, user?.uid],
  );

  useEffect(() => {
    const unsubChannels = subscribeToChannels(COMMUNITY_ID, (cats) => {
      setCategories(cats);
      setChannelsLoading(false);
    });
    const unsubMembers = subscribeToMemberPresence(COMMUNITY_ID, setMembers);
    return () => { unsubChannels(); unsubMembers(); };
  }, []);

  useEffect(() => {
    if (!user?.uid) return;
    let cleanup = () => {};
    initPresence(COMMUNITY_ID, user.uid).then(({ cleanup: fn }) => { cleanup = fn; });
    return () => cleanup();
  }, [user?.uid]);

  const paddingTop = Platform.OS === "web" ? totalHeaderHeight + 16 : 0;
  const paddingH = Platform.OS === "web" ? Math.max(16, theme.layout.pagePaddingX(bp.width) * 0.5) : 12;

  return (
    <View style={styles.root}>
      <ScreenBackground />
      <NavigationHeader />
      <View style={[styles.body, { paddingTop, paddingHorizontal: paddingH }]}>
        <ChannelsSidebar
          categories={categories}
          loading={channelsLoading}
          selectedChannelId={activeChannelId}
          onChannelSelect={setActiveChannelId}
        />
        <ChannelContent
          communityId={COMMUNITY_ID}
          categoryId={activeCategoryId}
          channelId={activeChannelId}
          channelName={activeChannelName}
        />
        <MembersList members={displayMembers} />
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  root: {
    flex: 1,
    backgroundColor: "#000000",
    height: Platform.OS === "web" ? "100vh" : "100%",
    maxHeight: Platform.OS === "web" ? "100vh" : "100%",
    overflow: "hidden",
  },
  body: {
    flex: 1,
    flexDirection: "row",
    gap: 10,
    paddingBottom: 16,
    overflow: "hidden",
  },
});
